import { t } from 'i18n';
import { type IChatMessage } from 'common/modules/ReactShared';
import type { IAttachmentData } from 'students/api/attachments/types';

export const convertAnswerToMessage = (
  answer: string | Blob,
  file?: IAttachmentData | null
): IChatMessage => {
  return {
    author: null,
    createdAt: new Date().toISOString(),
    editable: true,
    from: 'outgoing',
    key: 'new-answer',
    state: 'done',
    media:
      typeof answer !== 'string'
        ? {
            type: 'audio',
            url: URL.createObjectURL(answer)
          }
        : undefined,
    file: file ? { metadata: file.metadata, url: null } : null,
    text: typeof answer === 'string' ? answer : null
  };
};

export const formatAvgResponseTime = (str: string | number | null): string => {
  const seconds = Number(str);

  if (!seconds || Number.isNaN(seconds)) {
    // fallback
    return `1-${t('datetime.distance_in_words.x_days', { count: 2 })}`;
  }

  if (seconds < 60) {
    return t('datetime.distance_in_words.x_seconds', { count: seconds });
  }

  const minutes = Math.round(seconds / 60);

  if (minutes <= 20) {
    return t('datetime.distance_in_words.x_minutes', { count: minutes });
  }

  if (minutes < 60) {
    const rounded = Math.ceil(minutes / 10) * 10;
    const from = Math.max(rounded - 10, 10);
    const to = rounded;
    return `${from}-${t('datetime.distance_in_words.x_minutes', { count: to })}`;
  }

  const hours = Math.round(seconds / 3600);

  if (hours < 24) {
    return t('datetime.distance_in_words.about_x_hours', { count: hours });
  }

  const days = Math.round(seconds / 86400);

  if (days <= 2) {
    return `1-${t('datetime.distance_in_words.x_days', { count: 2 })}`;
  }

  return t('datetime.distance_in_words.x_days', { count: days });
};

// Mirrors the API's StudentAssignment#accepts_submission?: a passed deadline only closes the hand-in when the
// assignment refuses late work.
export const isHandInClosed = (
  deadline: string | null | undefined,
  allowLateSubmissions: boolean,
  now: Date = new Date()
): boolean => !!deadline && new Date(deadline) < now && !allowLateSubmissions;
